import { useState, useCallback, useRef } from 'react';
import { apiClient } from '../lib/apiClient';

export function useChat(token) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const abortRef = useRef(null);

  const sendMessage = useCallback(async (text, context = null) => {
    if (!text.trim() || !token) return;

    const userMsg = { role: 'user', content: text, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      abortRef.current = new AbortController();
      const payload = { message: text, session_id: sessionId };
      if (context) payload.context = context;
      const result = await apiClient.post('/api/v1/aif/chat', payload, {
        signal: abortRef.current.signal,
      });

      const data = result.data;
      if (data.session_id) setSessionId(data.session_id);

      const assistantMsg = {
        role: 'assistant',
        content: data.message,
        intent: data.intent,
        model: data.model,
        quick_replies: data.quick_replies || [],
        data_changed: data.data_changed || false,
        cards: data.cards || [],
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      if (err.name === 'AbortError') return;

      // 402 = free tier limit reached — show clean upgrade message
      if (err.status === 402) {
        const detail = err.body?.detail || err.body || {};
        const limit = detail.limit || 10;
        const upgradeUrl = detail.upgrade_url || '/settings/billing';
        const errorMsg = {
          role: 'assistant',
          content: `Du hast dein Free-Limit erreicht (${limit} Nachrichten/Tag). Upgrade auf Premium für mehr.`,
          isError: true,
          isUpgrade: true,
          upgradeUrl,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMsg]);
        return;
      }

      // Other errors — extract string message
      const detail = err.body?.detail;
      const errStr = typeof detail === 'string' ? detail
        : detail?.message && typeof detail.message === 'string' ? detail.message
        : detail?.error && typeof detail.error === 'string' ? detail.error
        : err?.message || `Anfrage fehlgeschlagen (${err.status || 'Netzwerk'})`;
      const errorMsg = {
        role: 'assistant',
        content: errStr,
        isError: true,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }, [token, sessionId]);

  const sendFile = useCallback(async (file, text = '') => {
    if (!file || !token) return;

    const isImage = file.type.startsWith('image/');
    const preview = isImage ? URL.createObjectURL(file) : null;

    const userMsg = {
      role: 'user',
      content: text || (isImage ? 'Bild hochgeladen' : file.name),
      timestamp: Date.now(),
      file: {
        name: file.name,
        type: isImage ? 'image' : 'pdf',
        preview,
        size: file.size,
      },
    };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);

    try {
      abortRef.current = new AbortController();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('message', text);
      if (sessionId) formData.append('session_id', sessionId);

      const result = await apiClient.post('/api/v1/aif/chat/upload', formData, {
        queueOnOffline: true,
        entityType: 'chat-attachment',
        signal: abortRef.current.signal,
      });

      if (result.queued) return;

      const data = result.data;
      if (data.session_id) setSessionId(data.session_id);

      const assistantMsg = {
        role: 'assistant',
        content: data.message,
        intent: data.intent,
        model: data.model,
        timestamp: Date.now(),
        file: data.file_url ? {
          url: data.file_url,
          type: data.file_type,
          name: data.filename,
        } : undefined,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      const detail = err.body?.detail;
      const errText = typeof detail === 'string' ? detail
        : err?.message || 'Upload fehlgeschlagen. Bitte versuche es erneut.';
      const errorMsg = {
        role: 'assistant',
        content: errText,
        isError: true,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }, [token, sessionId]);

  const newSession = useCallback(() => {
    setMessages([]);
    setSessionId(null);
  }, []);

  const loadSession = useCallback((sid, msgs) => {
    setSessionId(sid);
    setMessages(msgs.map(m => ({
      role: m.role,
      content: m.content,
      intent: m.intent,
      timestamp: m.timestamp || Date.now(),
    })));
  }, []);

  return { messages, sending, sessionId, sendMessage, sendFile, newSession, loadSession };
}
